import * as z from "zod";
import { countByFiltersInput, cursorPagination, decimal, ean, flag, pagination, uuidV7 } from "../index";
import { attributesStatuses, attributeTaskStatuses } from "./enums";
import { code, codeName, version } from "./fields";

// SKU attributes and their task (M4, 50-api/catalog.md §4).

/** How many SKUs a write on a task takes at a time. */
export const MAX_SKUS_PER_WRITE = 500;

const skuIds = z.array(z.uuid()).min(1).max(MAX_SKUS_PER_WRITE);

/**
 * Orders of the task list; the id breaks ties. `dueAt` puts the tasks not complete first, the nearest due date first and
 * those without one last, then the complete ones (ADR-223).
 */
export const attributeTaskSorts = ["-openedAt", "dueAt", "completeness", "linesheet"] as const;

const attributeTaskFilters = {
  status: z.enum(attributeTaskStatuses).optional(),
  attributesStatus: z.enum(attributesStatuses).optional(),
  brand: z.uuid().optional(),
  season: z.string().max(20).optional(),
  /** The task of one linesheet, if it has one. */
  linesheet: z.uuid().optional(),
  /** Part of the linesheet name, without case and accents (ADR-117). */
  q: z.string().trim().min(1).max(100).optional(),
};

/** Attribute tasks visible to the Actor, the latest opened first; tasks of archived linesheets are left out. */
export const listAttributeTasksInput = z.object({
  ...attributeTaskFilters,
  sort: z.enum(attributeTaskSorts).default("-openedAt"),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListAttributeTasksInput = z.input<typeof listAttributeTasksInput>;

/** How many tasks the list has with the same filters, and when the one open for longest was opened. */
export const countAttributeTasksInput = z.object(attributeTaskFilters);
export type CountAttributeTasksInput = z.input<typeof countAttributeTasksInput>;
/** The same count under several filters, in one call and one transaction (ADR-269): the queues of the home. */
export const countAttributeTasksByFiltersInput = countByFiltersInput(countAttributeTasksInput);
export type CountAttributeTasksByFiltersInput = z.input<typeof countAttributeTasksByFiltersInput>;

/**
 * Completeness of the attributes by brand (v0.2 §9, ADR-223): one row per brand with tasks on linesheets not
 * archived, the brand whose next due date is nearest first.
 */
export const listAttributeCompletenessInput = z.object({
  brand: attributeTaskFilters.brand,
  season: attributeTaskFilters.season,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListAttributeCompletenessInput = z.input<typeof listAttributeCompletenessInput>;

export const getAttributeTaskInput = z.object({ taskId: z.uuid() });
export type GetAttributeTaskInput = z.input<typeof getAttributeTaskInput>;

/** The SKUs of a task in the order of the linesheet: line, then size. */
export const listTaskSkusInput = z.object({
  taskId: z.uuid(),
  /** Only the unlocked SKUs still missing a required attribute. */
  onlyIncomplete: flag.optional(),
  onlyUnlocked: flag.optional(),
  /** Part of the style code or name, of the SKU code or of the EAN, without case and accents; codes by letters and digits (ADR-117). */
  q: z.string().trim().min(1).max(100).optional(),
  ...cursorPagination({ defaultLimit: 100, maxLimit: 500 }),
});
export type ListTaskSkusInput = z.input<typeof listTaskSkusInput>;
export const taskSkusQuery = listTaskSkusInput.omit({ taskId: true });

/** The task is opened by hand until Q-ARC-07 says what starts production; the id comes from the client (ADR-046). */
export const openAttributeTaskInput = z.object({ id: uuidV7, linesheetId: z.uuid() });
export type OpenAttributeTaskInput = z.input<typeof openAttributeTaskInput>;

/** Text of an attribute: an empty one is no value. */
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable();
const countryCode = z.string().length(2).nullable();
/** HS and commodity codes: digits, with the dots some tariffs print. */
const tariffCode = z
  .string()
  .trim()
  .regex(/^[0-9.]{4,20}$/)
  .nullable();
const weight = decimal.regex(/^\d{1,7}(\.\d{1,3})?$/).nullable();

/**
 * The attributes of an SKU as they are written: an absent field stays as it is, `null` empties it. The app checks a
 * cell against the same formats before it saves.
 */
export const skuAttributesFields = z
  .object({
    /** On the SKU itself: unique across the catalog. */
    ean: ean.nullable(),
    customsClassification: code.nullable(),
    hsCode: tariffCode,
    commodityCode: tariffCode,
    manufacturerId: z.uuid().nullable(),
    supplierStyleCode: text(80),
    supplierCode: text(40),
    purchaseDescription: text(5000),
    tomorrowColourDescription: text(200),
    matrixColourDescription: text(200),
    composition: text(300),
    compositionExtras: text(5000),
    washingCareInstructions: text(5000),
    animalScientificName: text(200),
    animalCountryOfOrigin: countryCode,
    /** Percentage points, 0 to 100. */
    animalMaterialPct: decimal
      .regex(/^\d{1,3}(\.\d{1,2})?$/)
      .refine((v) => Number(v) <= 100, "A percentage is at most 100")
      .nullable(),
    farmRaised: z.boolean().nullable(),
    dyed: z.boolean().nullable(),
    countryWhenceConsigned: countryCode,
    weightGross: weight,
    weightNet: weight,
    weightUnit: code.nullable(),
  })
  .partial();

const skuAttributesInput = skuAttributesFields.extend({
  skuId: z.uuid(),
  /** The one the client read: 409 otherwise. */
  version,
});
export type SkuAttributesInput = z.output<typeof skuAttributesInput>;

/** FR-M4-02, FR-M4-05: the brand fills in the unlocked SKUs, Tomorrow any of them; up to 500 at a time, all or none. */
export const saveSkuAttributesInput = z.object({
  taskId: z.uuid(),
  rows: z.array(skuAttributesInput).min(1).max(MAX_SKUS_PER_WRITE),
});
export type SaveSkuAttributesInput = z.input<typeof saveSkuAttributesInput>;
export const saveSkuAttributesBody = saveSkuAttributesInput.omit({ taskId: true });

/** FR-M4-02: Tomorrow locks and unlocks the SKUs of a task; SKUs of other linesheets are left as they are. */
export const lockSkuAttributesInput = z.object({ taskId: z.uuid(), skuIds });
export type LockSkuAttributesInput = z.input<typeof lockSkuAttributesInput>;
export const lockSkuAttributesBody = lockSkuAttributesInput.omit({ taskId: true });

/** An action on a task with the version the client read (409 otherwise). */
export const attributeTaskActionInput = z.object({ taskId: z.uuid(), version });
export type AttributeTaskActionInput = z.input<typeof attributeTaskActionInput>;

/** Back to an earlier status, with the reason. */
export const reopenAttributesInput = attributeTaskActionInput.extend({
  to: z.enum(["draft", "pending"]),
  note: z.string().trim().min(1).max(2000),
});
export type ReopenAttributesInput = z.input<typeof reopenAttributesInput>;
export const reopenAttributesBody = reopenAttributesInput.omit({ taskId: true });

/** The values of the attribute grid (ADR-048): nothing to choose. */
export const getAttributeOptionsInput = z.object({});
export type GetAttributeOptionsInput = z.input<typeof getAttributeOptionsInput>;

/**
 * The attribute task of a linesheet. Two axes (catalog.md §4): `status` says how far the unlocked SKUs are filled in,
 * `attributesStatus` (kept on the linesheet) follows submission and sync.
 */
export const attributeTaskDto = z
  .object({
    taskId: z.uuid(),
    version: z.number().int(),
    linesheetId: z.uuid(),
    linesheet: z.string(),
    brandOrgId: z.uuid(),
    brand: z.string(),
    season: z.string(),
    status: z.enum(attributeTaskStatuses),
    attributesStatus: z.enum(attributesStatuses),
    archived: z.boolean(),
    openedAt: z.iso.datetime(),
    dueAt: z.iso.datetime().nullable(),
    /** Unlocked SKUs with every required attribute, over the unlocked ones (FR-M4-04). */
    completenessPct: decimal,
    skuCount: z.number().int(),
    unlockedCount: z.number().int(),
    /** Unlocked SKUs still missing a required attribute. */
    incompleteCount: z.number().int(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "AttributeTask" });

/** The attribute tasks of a brand added up (ADR-223). */
export const attributeCompletenessDto = z
  .object({
    brandOrgId: z.uuid(),
    brand: z.string(),
    taskCount: z.number().int(),
    /** Tasks not complete yet, `overdue` among them. */
    openTaskCount: z.number().int(),
    overdueTaskCount: z.number().int(),
    skuCount: z.number().int(),
    unlockedCount: z.number().int(),
    incompleteCount: z.number().int(),
    /** Unlocked SKUs with every required attribute over the unlocked ones of all the tasks: their mean weighted by SKU. */
    completenessPct: decimal,
    /** The earliest due date among the tasks not complete; null when none has one. */
    nextDueAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "AttributeCompleteness" });

/** An SKU of a task with its attributes. Reference data by code, the manufacturer by id with its name. */
export const skuAttributesDto = z
  .object({
    skuId: z.uuid(),
    /** Its place in the task, from 1: what the issues of the task name. */
    row: z.number().int(),
    version: z.number().int(),
    /** FR-M4-02: the brand fills in only the unlocked SKUs. */
    locked: z.boolean(),
    unlockedAt: z.iso.datetime().nullable().optional(),
    skuCode: z.string().nullable().optional(),
    styleCode: z.string().optional(),
    styleName: z.string().nullable().optional(),
    colourDescription: z.string().nullable().optional(),
    size: z.string().optional(),
    productCategory: z.string().nullable().optional(),
    ean: z.string().nullable().optional(),
    customsClassification: z.string().nullable().optional(),
    hsCode: z.string().nullable().optional(),
    commodityCode: z.string().nullable().optional(),
    manufacturerId: z.uuid().nullable().optional(),
    manufacturer: z.string().nullable().optional(),
    supplierStyleCode: z.string().nullable().optional(),
    supplierCode: z.string().nullable().optional(),
    purchaseDescription: z.string().nullable().optional(),
    tomorrowColourDescription: z.string().nullable().optional(),
    matrixColourDescription: z.string().nullable().optional(),
    composition: z.string().nullable().optional(),
    compositionExtras: z.string().nullable().optional(),
    washingCareInstructions: z.string().nullable().optional(),
    animalScientificName: z.string().nullable().optional(),
    animalCountryOfOrigin: z.string().nullable().optional(),
    animalMaterialPct: decimal.nullable().optional(),
    farmRaised: z.boolean().nullable().optional(),
    dyed: z.boolean().nullable().optional(),
    countryWhenceConsigned: z.string().nullable().optional(),
    weightGross: decimal.nullable().optional(),
    weightNet: decimal.nullable().optional(),
    weightUnit: z.string().nullable().optional(),
    /** The required attributes still missing (`missingAttributes`), whether the SKU is locked or not. */
    missing: z.array(z.string()),
  })
  .meta({ id: "SkuAttributes" });

/** The SKUs a write changed, in the order of the input, and the task with its progress after the write. */
export const taskSkusDto = z.object({ rows: z.array(skuAttributesDto), task: attributeTaskDto });

/** The values the attribute grid offers (ADR-048). An external Actor sees the manufacturers of its own suppliers. */
export const attributeOptionsDto = z
  .object({
    customsClassifications: z.array(codeName),
    manufacturers: z.array(z.object({ id: z.uuid(), code: z.string().nullable(), name: z.string() })),
    countries: z.array(codeName),
    weightUnits: z.array(codeName),
  })
  .meta({ id: "AttributeOptions" });
